import {
  ComponentPropsOptions,
  computed,
  defineComponent,
  h
} from '@vue/composition-api';
import { BezierEdgeProps } from '../../types';
import { Position } from '../../types';
import BaseEdge from './BaseEdge.vue';
import { getBezierPath } from './utils';

const LoopbackBezierEdge = defineComponent<BezierEdgeProps>({
  name: 'LoopBackBezierEdge',

  props: [
    'sourcePosition',
    'targetPosition',
    'label',
    'labelStyle',
    'labelShowBg',
    'labelBgStyle',
    'labelBgPadding',
    'labelBgBorderRadius',
    'sourceY',
    'sourceX',
    'targetX',
    'targetY',
    'curvature',
    'markerEnd',
    'markerStart',
    'interactionWidth',
    'sourceHandle',
    'targetHandle',
    'data'
  ] as ComponentPropsOptions<BezierEdgeProps>,

  setup(props, { attrs }) {
    return () => {
      const [path, labelX, labelY] = getBezierPath({
        ...props,
        sourcePosition: props.sourcePosition ?? Position.Bottom,
        targetPosition: props.targetPosition ?? Position.Top
      });

      const loopbackPath = computed(() => {
        // for vertical loopback edges
        const radiusX = (props.sourceX - props.targetX) * 0.6;
        const radiusY = 50;

        return `M ${props.sourceX}, ${props.sourceY} A ${radiusX} ${radiusY} 0 1 0 ${props.targetX}, ${props.targetY}`;
      });

      const loopbackMidpoint = computed(() => {
        const x1 = props.sourceX;
        const y1 = props.sourceY;
        const x2 = props.targetX;
        const y2 = props.targetY;

        const radiusX = (x1 - x2) * 0.6;
        const radiusY = 90;

        // Midpoint of the line between source and target
        const mx = (x1 + x2) / 2;
        const my = (y1 + y2) / 2;

        // Perpendicular offset for arc center approximation
        const dx = x2 - x1;
        const dy = y2 - y1;
        const length = Math.sqrt(dx * dx + dy * dy);

        const offsetX = (dy / length) * radiusY;
        const offsetY = -(dx / length) * radiusY;

        return {
          x: mx + offsetX,
          y: my - offsetY
        };
      });

      return h(BaseEdge, {
        attrs: {
          path: loopbackPath.value,
          labelX: loopbackMidpoint.value.x,
          labelY: loopbackMidpoint.value.y,
          ...attrs
        },
        props: props
      });
    };
  }
});

export default LoopbackBezierEdge;
